"use client";

import { useSearchParams } from "next/navigation";
import { useEffect } from "react";

const REFERRAL_CODE_KEY = "agendei_referral_code";
const REFERRAL_EXPIRY_KEY = "agendei_referral_expiry";
const REFERRAL_EXPIRY_DAYS = 30; // Code expires after 30 days

/**
 * Component to capture and persist referral code from URL
 * Usage: Add to layout or page where ?ref=CODE parameter is expected
 */
export function ReferralCodeCapture() {
  const searchParams = useSearchParams();

  useEffect(() => {
    const refCode = searchParams.get("ref");

    if (refCode) {
      const cleanCode = refCode.trim().toUpperCase();

      // Save code with expiry date
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + REFERRAL_EXPIRY_DAYS);

      localStorage.setItem(REFERRAL_CODE_KEY, cleanCode);
      localStorage.setItem(REFERRAL_EXPIRY_KEY, expiryDate.toISOString());

      fetch(`/api/referrals/track?code=${cleanCode}`).catch((error) => {
        console.error("[Referral] Failed to track click:", error);
      });
    }
  }, [searchParams]);

  return null; // This component doesn't render anything
}

/**
 * Hook to get the stored referral code
 * Automatically checks expiry and clears expired codes
 */
export function useReferralCode(): string | null {
  useEffect(() => {
    // Check if code has expired
    const expiryStr = localStorage.getItem(REFERRAL_EXPIRY_KEY);
    if (expiryStr) {
      const expiryDate = new Date(expiryStr);
      if (expiryDate < new Date()) {
        // Code expired, clear it
        localStorage.removeItem(REFERRAL_CODE_KEY);
        localStorage.removeItem(REFERRAL_EXPIRY_KEY);
      }
    }
  }, []);

  if (typeof window === "undefined") return null;

  return localStorage.getItem(REFERRAL_CODE_KEY);
}
